import { z } from 'zod';
import { id, isoDateTime } from './common.js';
/**
 * The AI companion.
 *
 * Named "companion", not "therapist", and that is not just wording — it sets
 * what the thing is allowed to do. It reflects, asks questions, and walks you
 * through CBT exercises you already have on hand. It does not diagnose,
 * assess risk, advise on medication, or act as a substitute for the specialist
 * you already see. Those boundaries are enforced in the system prompt and
 * re-checked on output, because a model that drifts into clinical authority
 * with someone in distress is the main way this feature could do harm.
 *
 * The user flagged this as risky themselves. Accordingly it is behind a flag
 * (FEATURE_AI_COMPANION), every thread is deletable, memory is opt-in and
 * inspectable, and any hint of crisis short-circuits to real human resources
 * rather than continuing the conversation.
 */
export const chatRole = z.enum(['user', 'assistant', 'system']);
export const sendMessage = z.object({
    threadId: id.nullable().default(null),
    content: z.string().min(1).max(8000),
    /** Pulls today's check-ins/records into context. Off by default. */
    includeContext: z.boolean().default(false),
});
/**
 * Long-term memory: durable facts the companion may carry between sessions.
 *
 * Every entry is visible and individually deletable. Nothing is written
 * silently — memory candidates are proposed and the user confirms. The failure
 * mode being avoided is a model quietly accumulating an inaccurate profile of
 * someone and then reasoning from it for months.
 */
export const memoryKind = z.enum([
    'profile', // stable facts: work, household, diagnosis history
    'pattern', // "spirals after unstructured weekends"
    'trigger', // known sore spots
    'preference', // "wants directness, not reassurance"
    'coping', // what has actually worked before
    'goal',
]);
export const createMemory = z.object({
    kind: memoryKind,
    content: z.string().min(1).max(1000),
    sourceRef: z.string().max(200).nullable().default(null),
});
export const chatConfig = z.object({
    enabled: z.boolean(),
    memoryEnabled: z.boolean(),
    model: z.string().nullable(),
    provider: z.string().nullable(),
});
export const messageQuery = z.object({
    threadId: id,
    before: isoDateTime.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
//# sourceMappingURL=chat.js.map